import { z } from 'zod';
import type { Asset } from '../types/Asset';
import {
    GetResourceLibrarySitePageItemSchema,
    ResourceLibrarySitePageBaseItemSchema,
    ResourceLibrarySitePageItemSchema,
    ResourceLibrarySitePageSectionNode,
    ResourceLibrarySitePageTemplateSchema,
} from '../schemas/resourceLibrarySitePageItemZodSchemas';
import {
    ResourceLibrarySitePageApplicationArticleItem,
    ResourceLibrarySitePageBlock,
    ResourceLibrarySitePageBaseItem,
    ResourceLibrarySitePageGenericItem,
    ResourceLibrarySitePageHeadingSection,
    ResourceLibrarySitePageInternalLinkBlock,
    ResourceLibrarySitePageItem,
    ResourceLibrarySitePageLink,
    ResourceLibrarySitePagePlainTextImageSection,
    ResourceLibrarySitePagePlainTextSection,
    ResourceLibrarySitePageRichTableBlock,
    ResourceLibrarySitePageRichTextImageSection,
    ResourceLibrarySitePageRichTextSection,
    ResourceLibrarySitePageTableBlock,
    ResourceLibrarySitePageVideoBlock,
    ResourceLibrarySitePageTemplate,
    ResourceLibrarySitePageImageBlock,
} from '../types/resourceLibrarySitePageItems';
import {
    ProductGroupSchema,
    ProductApplicationSchema,
    NavigationNodeSchema,
} from '../schemas/resourceLibraryBaseZodSchemas';
import { sanitizeRichText } from '../../siteContent/tools/sanitizeRichText';
import { parseBodyToParagraphs } from '../../../common/tools/transformUtils';
import sanitizeRoute from '../../../common/tools/sanitizeRoute';

const TABLE_ALLOWED_EXTRA_TAGS = [
    'table',
    'thead',
    'tbody',
    'tfoot',
    'tr',
    'td',
    'th',
    'caption',
    'colgroup',
    'col',
    'span',
    'div',
    'ul',
    'ol',
    'li',
    'a',
];

const TABLE_ALLOWED_EXTRA_ATTRIBUTES = [
    // for a
    'href',
    'target',
    'rel',

    // for td, th
    'colspan',
    'rowspan',
    'scope', // only for th

    'title',
];

type HeadingSectionNode = Extract<
    ResourceLibrarySitePageSectionNode,
    { __typename: 'fieldcollection_HeadingSection' }
>;
type PlainTextSectionNode = Extract<
    ResourceLibrarySitePageSectionNode,
    { __typename: 'fieldcollection_PlainTextSection' }
>;
type RichTextSectionNode = Extract<
    ResourceLibrarySitePageSectionNode,
    { __typename: 'fieldcollection_RichTextSection' }
>;
type PlainTextImageSectionNode = Extract<
    ResourceLibrarySitePageSectionNode,
    { __typename: 'fieldcollection_PlainTextImageSection' }
>;
type RichTextImageSectionNode = Extract<
    ResourceLibrarySitePageSectionNode,
    { __typename: 'fieldcollection_RichTextImageSection' }
>;
type VideoBlockNode = Extract<
    ResourceLibrarySitePageSectionNode,
    { __typename: 'fieldcollection_VideoBlock' }
>;
type ImageBlockNode = Extract<
    ResourceLibrarySitePageSectionNode,
    { __typename: 'fieldcollection_ImageBlock' }
>;
type InternalLinkBlockNode = Extract<
    ResourceLibrarySitePageSectionNode,
    { __typename: 'fieldcollection_InternalLinkBlock' }
>;
type TableBlockNode = Extract<
    ResourceLibrarySitePageSectionNode,
    { __typename: 'fieldcollection_TableBlock' }
>;
type RichTableBlockNode = Extract<
    ResourceLibrarySitePageSectionNode,
    { __typename: 'fieldcollection_RichTableBlock' }
>;

interface FieldCollectionLinkNode {
    path: string;
    target: string | null;
    anchor: string | null;
    parameters: string | null;
}

type RawResourceLibrarySitePageTemplate = z.infer<typeof ResourceLibrarySitePageTemplateSchema>;

const GENERIC_TEMPLATE_ALIASES = new Set<RawResourceLibrarySitePageTemplate>([
    'SimpleTextVideo',
    'ImageGalleryArticle',
    'MixedMediaArticle',
]);

function normalizeResourceLibrarySitePageTemplate(
    template: RawResourceLibrarySitePageTemplate
): ResourceLibrarySitePageTemplate {
    if (template === 'ApplicationArticle') {
        return 'ApplicationArticle';
    }

    if (template === 'Generic') {
        return 'Generic';
    }

    if (GENERIC_TEMPLATE_ALIASES.has(template)) {
        return 'Generic';
    }

    console.warn(
        'Unknown ResourceLibrarySitePageItem template encountered. Falling back to Generic template.',
        {
            template,
        }
    );

    return 'Generic';
}

function normalizeDescription(description: string | null): string {
    return description ?? '';
}

function mapIds<T extends z.ZodTypeAny>(
    schema: T,
    items: z.infer<T>[] | null | undefined
): string[] {
    if (!items) {
        return [];
    }
    return items.map((entry) => schema.parse(entry).id);
}

function extractMetadataValue(
    metadata: { name: string; data: string }[] | null | undefined,
    key: string
): string | null {
    if (!metadata) {
        return null;
    }

    const entry = metadata.find((item) => item.name === key);
    return entry?.data ?? null;
}

function extractAltText(
    metadata: { name: string; data: string }[] | null | undefined,
    fallbackTitle: string | null
): string | null {
    const alt = extractMetadataValue(metadata, 'alt');
    if (alt && alt.trim().length > 0) {
        return alt;
    }
    return fallbackTitle && fallbackTitle.trim().length > 0 ? fallbackTitle : null;
}

function normalizeLinkParameters(parameters: string | null | undefined): string | null {
    const raw = parameters?.trim() ?? '';
    if (!raw) {
        return null;
    }

    const normalized = raw.startsWith('?') ? raw.slice(1) : raw;
    return normalized;
}

function isExternalUrl(path: string): boolean {
    if (!path) {
        return false;
    }
    const trimmed = path.trim();
    return trimmed.startsWith('//') || /^[a-z][a-z0-9+.-]*:/i.test(trimmed);
}

function normalizeLink(
    link: FieldCollectionLinkNode | null | undefined
): ResourceLibrarySitePageLink | null {
    if (!link) {
        return null;
    }

    const rawPath = link.path?.trim() ?? '';
    if (!rawPath) {
        return null;
    }

    const target = link.target?.trim() ?? '';
    const normalizedTarget = target.toLowerCase();
    const openInNewTab = normalizedTarget === '_blank';
    if (target !== '' && target !== '_blank') {
        console.warn('Unsupported link target on ResourceLibrarySitePage section link', {
            target,
        });
    }

    const anchor = link.anchor?.trim() ?? null;
    const parameters = normalizeLinkParameters(link.parameters);

    if (isExternalUrl(rawPath)) {
        return {
            kind: 'external',
            rawPath,
            anchor,
            openInNewTab,
            parameters,
            url: rawPath,
        };
    }

    const sanitizedRoute = sanitizeRoute(rawPath);
    return {
        kind: 'internal',
        rawPath,
        anchor,
        openInNewTab,
        parameters,
        route: sanitizedRoute,
    };
}

function sanitizeSectionContent(content: string): string {
    return sanitizeRichText(
        content,
        ['ul', 'li', 'a'],
        ['href', 'target', 'rel', 'pimcore_id', 'pimcore_type']
    );
}

function hydrateResourceLibrarySitePageSection(
    section: ResourceLibrarySitePageSectionNode,
    index: number
): ResourceLibrarySitePageBlock | null {
    switch (section.__typename) {
        case 'fieldcollection_HeadingSection': {
            const headingSection = section as HeadingSectionNode;
            return {
                kind: 'headingSection',
                level: headingSection.level,
                mergeWithPrevious: headingSection.mergeWithPrevious,
                title: headingSection.title,
            } satisfies ResourceLibrarySitePageHeadingSection;
        }
        case 'fieldcollection_PlainTextSection': {
            const plainTextSection = section as PlainTextSectionNode;
            return {
                kind: 'plainTextSection',
                level: plainTextSection.level,
                mergeWithPrevious: plainTextSection.mergeWithPrevious,
                title: plainTextSection.title ?? null,
                content: plainTextSection.content,
                contentParagraphs: parseBodyToParagraphs(plainTextSection.content),
            } satisfies ResourceLibrarySitePagePlainTextSection;
        }
        case 'fieldcollection_RichTextSection': {
            const richTextSection = section as RichTextSectionNode;
            return {
                kind: 'richTextSection',
                level: richTextSection.level,
                mergeWithPrevious: richTextSection.mergeWithPrevious,
                title: richTextSection.title ?? null,
                content: richTextSection.content,
                sanitizedContent: sanitizeSectionContent(richTextSection.content),
            } satisfies ResourceLibrarySitePageRichTextSection;
        }
        case 'fieldcollection_PlainTextImageSection': {
            const plainTextImageSection = section as PlainTextImageSectionNode;
            return {
                kind: 'plainTextImageSection',
                level: plainTextImageSection.level,
                mergeWithPrevious: plainTextImageSection.mergeWithPrevious,
                title: plainTextImageSection.title ?? null,
                content: plainTextImageSection.content,
                contentParagraphs: parseBodyToParagraphs(plainTextImageSection.content),
                imageUrl: plainTextImageSection.image.image.fullpath,
                imageAltText: extractAltText(
                    plainTextImageSection.image.image.metadata ?? null,
                    plainTextImageSection.imageTitle ?? null
                ),
                imageTitle: plainTextImageSection.imageTitle ?? null,
                imageCaption: plainTextImageSection.imageCaption ?? null,
                imageLink: normalizeLink(plainTextImageSection.imageLink ?? null),
            } satisfies ResourceLibrarySitePagePlainTextImageSection;
        }
        case 'fieldcollection_RichTextImageSection': {
            const richTextImageSection = section as RichTextImageSectionNode;
            const sanitizedContent = sanitizeSectionContent(richTextImageSection.content);
            return {
                kind: 'richTextImageSection',
                level: richTextImageSection.level,
                mergeWithPrevious: richTextImageSection.mergeWithPrevious,
                title: richTextImageSection.title ?? null,
                content: richTextImageSection.content,
                sanitizedContent,
                imageUrl: richTextImageSection.image.image.fullpath,
                imageAltText: extractAltText(
                    richTextImageSection.image.image.metadata ?? null,
                    richTextImageSection.imageTitle ?? null
                ),
                imageTitle: richTextImageSection.imageTitle ?? null,
                imageCaption: richTextImageSection.imageCaption ?? null,
                imageLink: normalizeLink(richTextImageSection.imageLink ?? null),
            } satisfies ResourceLibrarySitePageRichTextImageSection;
        }
        case 'fieldcollection_ImageBlock': {
            return hydrateResourceLibrarySitePageImageBlock(section as ImageBlockNode);
        }
        case 'fieldcollection_InternalLinkBlock': {
            const internalLinkBlock = section as InternalLinkBlockNode;
            return hydrateResourceLibrarySitePageInternalLinkBlock(internalLinkBlock);
        }
        case 'fieldcollection_VideoBlock': {
            return hydrateResourceLibrarySitePageVideoBlock(section as VideoBlockNode, index);
        }
        case 'fieldcollection_TableBlock': {
            return hydrateResourceLibrarySitePageTableBlock(section as TableBlockNode, index);
        }
        case 'fieldcollection_RichTableBlock': {
            return hydrateResourceLibrarySitePageRichTableBlock(
                section as RichTableBlockNode,
                index
            );
        }
        default: {
            return assertUnsupportedSection(section);
        }
    }
}

function assertUnsupportedSection(x: never): never {
    throw new Error('Unexpected section: ' + x);
}

function normalizeVideoBlockVideo(
    video: VideoBlockNode['video'],
    index: number
): ResourceLibrarySitePageVideoBlock['video'] | null {
    if (!video) {
        console.warn('VideoBlock missing video payload', { index });
        return null;
    }

    const type = video.type?.toLowerCase() ?? '';
    const data = video.data;

    if (!data) {
        console.warn('VideoBlock missing data payload', { index });
        return null;
    }

    if (type === 'asset') {
        if (data.__typename !== 'asset') {
            console.warn('VideoBlock asset type mismatch', {
                index,
                typename: data.__typename,
            });
            return null;
        }

        const { id, fullpath, mimetype, type: assetType } = data;
        if (!id || !fullpath || !mimetype || !assetType) {
            console.warn('VideoBlock asset missing required fields', {
                index,
                id,
                fullpath,
                mimetype,
                assetType,
            });
            return null;
        }

        const normalizedAsset: Asset = {
            id,
            fullpath,
            mimeType: mimetype,
            type: assetType as Asset['type'],
        };

        return {
            kind: 'internal',
            asset: normalizedAsset,
        };
    }

    if (type === 'youtube' || type === 'vimeo') {
        if (data.__typename !== 'VideoDataDescriptor') {
            console.warn('VideoBlock external type mismatch', {
                index,
                typename: data.__typename,
            });
            return null;
        }

        const videoId = data.videoId?.trim();
        if (!videoId) {
            console.warn('VideoBlock external entry missing videoId', { index });
            return null;
        }

        const embedUrl =
            type === 'youtube'
                ? `https://www.youtube.com/embed/${encodeURIComponent(videoId)}`
                : `https://player.vimeo.com/video/${encodeURIComponent(videoId)}`;

        return {
            kind: 'external',
            original: videoId,
            embedUrl,
            provider: type,
            videoId,
        };
    }

    console.warn('Unsupported video type in VideoBlock', {
        index,
        type: video.type,
    });
    return null;
}

function hydrateResourceLibrarySitePageVideoBlock(
    section: VideoBlockNode,
    index: number
): ResourceLibrarySitePageVideoBlock | null {
    const normalizedVideo = normalizeVideoBlockVideo(section.video, index);
    if (!normalizedVideo) {
        return null;
    }

    return {
        kind: 'videoBlock',
        video: normalizedVideo,
        ariaLabel: section.ariaLabel.trim(),
    } satisfies ResourceLibrarySitePageVideoBlock;
}

function parseTableRows(table: string | null | undefined, index: number): string[][] | null {
    if (!table) {
        console.warn('TableBlock missing table payload', { index });
        return null;
    }

    try {
        const parsed = JSON.parse(table);
        if (!Array.isArray(parsed)) {
            console.warn('TableBlock table payload is not an array of rows', { index });
            return null;
        }

        const rows: string[][] = [];
        for (let rowIndex = 0; rowIndex < parsed.length; rowIndex += 1) {
            const row = parsed[rowIndex];
            if (!Array.isArray(row)) {
                console.warn('TableBlock row payload is not an array', { index, rowIndex });
                return null;
            }

            rows.push(
                row.map((cell, cellIndex) => {
                    if (cell === null || cell === undefined) {
                        return '';
                    }

                    if (typeof cell === 'string') {
                        return cell;
                    }

                    console.warn('TableBlock cell is not a string, coercing to string', {
                        index,
                        rowIndex,
                        cellIndex,
                    });
                    return String(cell);
                })
            );
        }

        return rows;
    } catch (error) {
        console.warn('Failed to parse TableBlock payload', { index, error });
        return null;
    }
}

function hydrateResourceLibrarySitePageTableBlock(
    section: TableBlockNode,
    index: number
): ResourceLibrarySitePageTableBlock | null {
    const rows = parseTableRows(section.table, index);
    if (!rows || rows.length === 0) {
        console.warn('TableBlock has no rows after parsing', { index });
        return null;
    }

    const caption = section.caption?.trim() ?? null;

    return {
        kind: 'tableBlock',
        firstRowIsHeader: Boolean(section.firstRowIsHeader),
        firstColumnIsHeader: Boolean(section.firstColumnIsHeader),
        rows,
        caption: caption && caption.length > 0 ? caption : null,
    };
}

function hydrateResourceLibrarySitePageRichTableBlock(
    section: RichTableBlockNode,
    index: number
): ResourceLibrarySitePageRichTableBlock | null {
    const rawHtml = section.tableHtml?.trim();
    if (!rawHtml) {
        console.warn('RichTableBlock missing tableHtml payload', { index });
        return null;
    }

    const sanitizedHtml = sanitizeRichText(
        rawHtml,
        TABLE_ALLOWED_EXTRA_TAGS,
        TABLE_ALLOWED_EXTRA_ATTRIBUTES
    );
    if (!sanitizedHtml || sanitizedHtml.trim().length === 0) {
        console.warn('RichTableBlock sanitized HTML is empty', { index });
        return null;
    }

    const caption = section.caption?.trim() ?? null;

    return {
        kind: 'richTableBlock',
        tableHtml: sanitizedHtml,
        caption: caption && caption.length > 0 ? caption : null,
    };
}

function hydrateResourceLibrarySitePageImageBlock(
    section: ImageBlockNode
): ResourceLibrarySitePageImageBlock {
    return {
        kind: 'imageBlock',
        imageUrl: section.image.image.fullpath,
        imageAltText: extractAltText(
            section.image.image.metadata ?? null,
            section.imageTitle ?? null
        ),
        imageTitle: section.imageTitle ?? null,
        imageCaption: section.imageCaption ?? null,
        imageLink: normalizeLink(section.imageLink ?? null),
        thumbnailUrl: section.image.image.thumbnailUrl,
    } satisfies ResourceLibrarySitePageImageBlock;
}

function hydrateResourceLibrarySitePageInternalLinkBlock(
    section: InternalLinkBlockNode
): ResourceLibrarySitePageInternalLinkBlock {
    return {
        kind: 'internalLinkBlock',
        openInNewTab: section.openInNewTab,
        linkText: section.linkText,
        linkRoute: section.linkRoute ? sanitizeRoute(section.linkRoute) : section.linkRoute,
    };
}

function hydrateResourceLibrarySitePageSections(
    sections: ResourceLibrarySitePageSectionNode[]
): ResourceLibrarySitePageBlock[] {
    const hydratedSections: ResourceLibrarySitePageBlock[] = [];

    sections.forEach((section, index) => {
        const hydrated = hydrateResourceLibrarySitePageSection(section, index);
        if (hydrated) {
            hydratedSections.push(hydrated);
        }
    });

    return hydratedSections;
}

export function hydrateResourceLibrarySitePageBaseItem(
    item: z.infer<typeof ResourceLibrarySitePageBaseItemSchema>
): ResourceLibrarySitePageBaseItem {
    const template = normalizeResourceLibrarySitePageTemplate(item.template);

    return {
        kind: 'sitePage',
        id: item.id,
        key: item.key,
        name: item.name,
        description: normalizeDescription(item.description),
        brandId: item.brand ? item.brand.id : null,
        productGroupIds: mapIds(ProductGroupSchema, item.productGroup),
        productApplicationIds: mapIds(ProductApplicationSchema, item.productApplication),
        resourceTypeId: item.resourceType.id,
        navigationScopeIds: mapIds(NavigationNodeSchema, item.navigationScope),
        exclusiveNavigationScopeIds: mapIds(NavigationNodeSchema, item.exclusiveNavigationScope),
        publicationDate: item.publicationDate,
        thumbnailUrl: item.thumbnailUrl,
        coverImageUrl: item.coverImage?.fullpath ?? null,
        template,
        lastModificationDate: item.lastModificationDate ?? null,
    };
}

export function hydrateResourceLibrarySitePageItem(
    item: z.infer<typeof ResourceLibrarySitePageItemSchema>
): ResourceLibrarySitePageItem | null {
    const base = hydrateResourceLibrarySitePageBaseItem(item);
    const navigationNodeId = item.navigationNode ? item.navigationNode.id : null;
    const sections = hydrateResourceLibrarySitePageSections(item.sections);

    if (base.template === 'ApplicationArticle') {
        const applicationArticleItem: ResourceLibrarySitePageApplicationArticleItem = {
            ...base,
            template: 'ApplicationArticle',
            navigationNodeId,
            sections,
        };

        return applicationArticleItem;
    }

    const genericItem: ResourceLibrarySitePageGenericItem = {
        ...base,
        template: 'Generic',
        navigationNodeId,
        sections,
    };

    return genericItem;
}

export function transformResourceLibrarySitePageItemResponse(
    data: unknown
): ResourceLibrarySitePageItem | null {
    const parsedResult = GetResourceLibrarySitePageItemSchema.safeParse(data);

    if (!parsedResult.success) {
        console.error(
            'Validation failed for getResourceLibrarySitePageItem:',
            parsedResult.error.errors
        );
        return null;
    }

    const item = parsedResult.data.getResourceLibrarySitePageItem;
    return hydrateResourceLibrarySitePageItem(item);
}
